import { z } from "zod";
import { Latitude, Longitude } from "./geo";

// Drones on the map (issue #234, ADR-0019). An app on the DJI controller reads the drone's
// telemetry from the controller itself and sends it here; HQ sees the drone online in the panel
// and, after adding it to an action, its flight on the map and in the coverage.

const Id = z.uuid();
const Timestamp = z.iso.datetime({ offset: true });

/** 8 digits, typed into the controller once (the owner, 2026-10-12: nothing to paste there). */
export const DRONE_CODE_RE = /^\d{8}$/;

export const DronePairInput = z.object({ code: z.string().regex(DRONE_CODE_RE) });
export type DronePairInput = z.infer<typeof DronePairInput>;
export const DronePairResponse = z.object({ token: z.string(), name: z.string() });
export type DronePairResponse = z.infer<typeof DronePairResponse>;

/** One telemetry reading of the drone (about one per second). */
export const DroneSample = z.object({
  /** UUID made on the controller: a resent sample is not stored twice. */
  id: z.uuid(),
  /** Grows with every sample on the controller; orders the flight, never the clock. */
  seq: z.number().int().nonnegative(),
  at: Timestamp,
  lat: Latitude,
  lon: Longitude,
  /** Height above the take-off point, metres. */
  heightM: z.number().min(-1000).max(10_000),
  speedMps: z.number().nonnegative().max(200),
  heading: z.number().min(0).max(360),
  satellites: z.number().int().min(0).max(100),
  inAir: z.boolean(),
});
export type DroneSample = z.infer<typeof DroneSample>;

/** What the controller link does, for diagnosis without a cable (no ADB on the RC 2). */
export const DroneLink = z.object({
  /** The controller port that gave the last reading; null while none gives one. */
  port: z.number().int().nullable(),
  bytes: z.number().int().nonnegative(),
  records: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  /** First bytes of the stream as hex while no reading was recognised. */
  sampleHex: z.string().max(512).nullish(),
  /** What each port of the controller did, e.g. "8902: 0 B · 8903: odmowa". */
  ports: z.string().max(400).nullish(),
});
export type DroneLink = z.infer<typeof DroneLink>;

export const DroneTelemetryInput = z.object({
  appVersion: z.string().max(40),
  /** The controller's clock, to correct the readings' times like a phone's. */
  deviceNow: Timestamp,
  link: DroneLink,
  /** The pilot paused sending positions on the controller; the drone still reports online. */
  tracking: z.boolean().default(true),
  /** The controller's own battery (the drone's is not in this telemetry). */
  controllerBatteryPct: z.number().int().min(0).max(100).nullish(),
  /** Oldest first; the controller keeps what the server has not taken yet. */
  samples: z.array(DroneSample).max(600),
});
export type DroneTelemetryInput = z.infer<typeof DroneTelemetryInput>;
export const DroneTelemetryResponse = z.object({
  name: z.string(),
  /** The action the flight is recorded in; null shows "not in an action" on the controller. */
  actionName: z.string().nullable(),
});
export type DroneTelemetryResponse = z.infer<typeof DroneTelemetryResponse>;

/** Base64 of raw bytes the controller app received, at most 256 kB per port. */
const Base64 = z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/);

/**
 * One minute of raw bytes from every port of the controller (#247): with no ADB on the RC 2 this
 * is how the protocol is studied. Kept for analysis only; nothing in Ratun reads it.
 */
export const DroneCaptureInput = z.object({
  appVersion: z.string().max(40),
  startedAt: Timestamp,
  durationMs: z.number().int().nonnegative().max(600_000),
  ports: z
    .array(
      z.object({
        port: z.number().int().min(1).max(65_535),
        /** Why the port gave nothing ("ConnectException: …"); null when it connected. */
        error: z.string().max(200).nullable(),
        connections: z.number().int().nonnegative(),
        /** In order of arrival; `t` is milliseconds since the start of the capture. */
        chunks: z.array(z.object({ t: z.number().int().nonnegative(), b64: Base64 })).max(5_000),
      }),
    )
    .max(10),
});
export type DroneCaptureInput = z.infer<typeof DroneCaptureInput>;

/** The last report of the controller, kept on the drone. */
export const DroneState = z.object({
  appVersion: z.string(),
  link: DroneLink,
  tracking: z.boolean().default(true),
  controllerBatteryPct: z.number().int().min(0).max(100).nullish(),
  last: DroneSample.omit({ id: true, seq: true }).nullable(),
});
export type DroneState = z.infer<typeof DroneState>;

export const DroneDto = z.object({
  id: Id,
  name: z.string(),
  code: z.string(),
  paired: z.boolean(),
  lastSeenAt: Timestamp.nullable(),
  state: DroneState.nullable(),
  /** The action the drone flies for now, and its participant there (the marker on the map). */
  action: z.object({ id: Id, name: z.string(), participantId: Id }).nullable(),
  videoSourceId: Id.nullable(),
});
export type DroneDto = z.infer<typeof DroneDto>;

export const CreateDroneInput = z.object({ name: z.string().trim().min(2).max(60) });
export type CreateDroneInput = z.infer<typeof CreateDroneInput>;
/** Which camera stream is this drone's (#236); null unlinks. */
export const LinkDroneVideoInput = z.object({ videoSourceId: Id.nullable() });
export type LinkDroneVideoInput = z.infer<typeof LinkDroneVideoInput>;

/** The app on the controller reports at least this often; quieter means offline. */
export const DRONE_ONLINE_S = 30;
